import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { SuperBillControllerApi } from "../../../services";
import {
  DGT_Pagination,
  getConfigObject,
  getPageIndexWhileSearch,
  getXtenantId,
  useBaseContent,
} from "../../../utils/utils";

export interface PaymentDue {
  superBillUuid: string;
  appointmentUuid?: string;
  renderingProviderName?: string;
  dateOfService?: string;
  amountDue: number;
  subTotal?: number;
}

interface UsePaymentsDueProps {
  patientUuid: string;
  pagination: DGT_Pagination;
  sortBy?: string;
  sortDirection?: "asc" | "desc";
  enabled?: boolean;
}

/**
 * Fetch outstanding patient invoices for the Patient Portal Dashboard
 * "Payments Due" card. Returns invoices where status is PENDING or
 * PARTIALLY_PAID AND patientBalance > 0 AND archive = false.
 *
 * @see ECRH-4398
 */
export function usePaymentsDue(
  props: UsePaymentsDueProps
): useBaseContent<PaymentDue> {
  const prevPage = useRef<number>(props.pagination.page);

  const pageIndex = getPageIndexWhileSearch({
    pagination: props.pagination,
    prevPage,
  });
  const pageSize = props.pagination.pageSize;

  return useQuery({
    queryKey: ["payments-due", pageSize, pageIndex, props],
    enabled: !!props.patientUuid && props.enabled !== false,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      return new SuperBillControllerApi(getConfigObject())
        .getPaymentsDue(
          props.patientUuid,
          pageIndex,
          pageSize,
          props.sortBy,
          props.sortDirection,
          getXtenantId()
        )
        .then((res) => res?.data as any);
    },
  });
}
